import Dexie, { type EntityTable } from 'dexie'
if (import.meta.client) {
  await import('dexie-observable')
}
import { defineStore } from 'pinia'

export interface CurationEntry {
  id: string
  sort: number
  type: 'NOTE' | 'GALLERY'
  workspace: string
  schemaVersion: number
  c: Curation
}

type EventType = 'CREATED' | 'UPDATED' | 'DELETED'

export type CurationDatabaseListener = (key: string, type: EventType) => void

const types: EventType[] = ['CREATED', 'UPDATED', 'DELETED']

const curationSchemaVersion = 3

export const useCurationStore = defineStore(
  'curation',
  () => {

    const listeners: CurationDatabaseListener[] = []

    const addListener = (l: CurationDatabaseListener) => {
      listeners.push(l)
    }

    const removeListener = (l: CurationDatabaseListener) => {
      removeFromArray(listeners, l)
    }

    const setTimestamp = (c: Curation) => {
      if (!c.edit) c.edit = {}
      c.edit.date = now()
    }

    if (import.meta.client) {
      const db = new Dexie('jps-curation-database') as Dexie & {
        curations: EntityTable<CurationEntry, 'id'>
      }
      db.version(1).stores({
        curations: '&id, sort, type, workspace',
      })
      db.on('changes', (changes) => {
        changes.forEach((change) => {
          listeners.forEach((l) => {
            l(change.key, types[change.type - 1])
          })
        })
      })

      const listNotes = async () => {
        const notes = await db.curations.orderBy('sort').toArray()
        notes.forEach((n) => (n.c.parts = []))
        return notes
      }

      const updateNote = async (c: Curation, sort: number) => {
        const entry = await db.curations.get(c.id)
        if (entry.c.edit.date <= c.edit.date) {
          setTimestamp(c)
          await db.curations.update(c.id, {
            c: c,
            sort: sort,
          })
        }
      }

      const addNote = async (c: Curation, sort: number) => {
        setTimestamp(c)
        await db.curations.add({
          id: c.id,
          sort: sort,
          c: c,
          type: 'NOTE',
          schemaVersion: curationSchemaVersion,
          workspace: null,
        } as CurationEntry)
      }

      const deleteCuration = async (id: string) => {
        await db.curations.delete(id)
      }

      const getCuration = async (id: string) => {
        return db.curations.get(id)
      }

      const listGalleries = async (workspace: string): Promise<CurationEntry[]> => {
        const notes = await db.curations
          .where('workspace')
          .equals(workspace)
          .sortBy('sort')
        notes.forEach((n) => (n.c.parts = null))
        return notes
      }

      const updateGallery = async (c: Curation, sort: number, workspace: string) => {
        const entry = await db.curations.get(c.id)
        if (entry.c.edit.date <= c.edit.date) {
          //最終更新日のタイムスタンプを入れる
          setTimestamp(c)
          await db.curations.update(c.id, {
            c: clone(c),
            sort: sort,
            workspace: workspace,
          })
          return db.curations.get(c.id)
        }
      }

      const updateGallerySimple = async (c: Curation) => {
        //最終更新日のタイムスタンプを入れる
        setTimestamp(c)
        await db.curations.update(c.id, {
          c: clone(c),
        })
        const entry = await db.curations.get(c.id)
        delete entry.c.edit
        return entry
      }

      const addGallery = async (c: Curation, sort: number, workspace: string) => {
        setTimestamp(c)
        if (!c.id) c.id = hashId()
        await db.curations.add({
          id: c.id,
          sort: sort,
          c: c,
          type: 'GALLERY',
          schemaVersion: curationSchemaVersion,
          workspace: workspace,
        } as CurationEntry)
      }

      return {
        addListener,
        removeListener,
        listNotes,
        updateNote,
        addNote,
        deleteCuration,
        getCuration,
        listGalleries,
        updateGallery,
        updateGallerySimple,
        addGallery,
      }

    } else {

      const listNotes = async () => { return null }
      const updateNote = async (c: Curation, sort: number) => {}
      const addNote = async (c: Curation, sort: number) => {}
      const deleteCuration = async (id: string) => { return null }
      const getCuration = async (id: string) => { return null }
      const listGalleries = async (workspace: string): Promise<CurationEntry[]> => { return [] }
      const updateGallery = async (c: Curation, sort: number, workspace: string) => { return null }
      const updateGallerySimple = async (c: Curation) => { return null }
      const addGallery = async (c: Curation, sort: number, workspace: string) => {}

      return {
        addListener,
        removeListener,
        listNotes,
        updateNote,
        addNote,
        deleteCuration,
        getCuration,
        listGalleries,
        updateGallery,
        updateGallerySimple,
        addGallery,
      }

    }

  },
  { persist: false },
)
